import type {
  VotingDetail,
  VotingQuestionWithResults,
} from '../../api/DigiobcanApi';
import {
  declineAnswers,
  questionTypeLabel,
  scalePoints,
  totalMarks,
} from '../../lib/votings';
import { __, declension } from '../../utils';
import ResultBars from './ResultBars';
import { ScaleResultsSummary, ScaleResultsTable } from './ScaleResults';
import TextAnswers from './TextAnswers';
import { Button, Card, Stack, Typography } from '@presov.sk/presov-ui-react';
import React from 'react';

type Props = {
  questions: VotingQuestionWithResults[];
  votesCount: number;
  tone: 'citizen' | 'admin';
  /** Lístok tohto účtu - vyznačí vlastné odpovede v pruhoch. */
  myVotes?: VotingDetail['my_votes'];
};

/** Hlavička otázky: „Otázka 2 · viac možností, vybrané 1 - 3". */
const eyebrow = (question: VotingQuestionWithResults, index: number) => {
  const base = `${__('Otázka')} ${index + 1} · ${questionTypeLabel(question.type).toLocaleLowerCase('sk')}`;

  if (question.type === 'multi_choice') {
    return question.min_options === question.max_options
      ? `${base}, ${__('vybrané')} ${question.max_options}`
      : `${base}, ${__('vybrané')} ${question.min_options} - ${question.max_options}`;
  }

  if (question.type === 'scale') {
    const points = scalePoints(question);
    const first = points[0];
    const last = points[points.length - 1];
    const range = `${first} - ${last}`;
    const labels =
      question.scale_min_label || question.scale_max_label
        ? ` (${first} = ${question.scale_min_label ?? first}, ${last} = ${
            question.scale_max_label ?? last
          })`
        : '';
    return `${__('Otázka')} ${index + 1} · ${__('škála')} ${range}${labels}`;
  }

  return base;
};

const QuestionResultCard: React.FC<{
  question: VotingQuestionWithResults;
  index: number;
  votesCount: number;
  tone: 'citizen' | 'admin';
  myOptionIds?: ReadonlySet<number>;
}> = ({ question, index, votesCount, tone, myOptionIds }) => {
  const [openAnswers, setOpenAnswers] = React.useState(false);

  const textAnswers = React.useMemo(
    () => question.options.flatMap((option) => option.text_answers ?? []),
    [question.options],
  );

  const isTextOnly =
    question.type === 'free_text' ||
    (question.options.length === 1 && question.options[0].type === 'free');

  const marks = totalMarks(question);

  return (
    <Card sx={{ gap: 2.25, p: 3 }}>
      <Stack
        direction='row'
        alignItems='baseline'
        justifyContent='space-between'
        gap={2}
      >
        <Stack gap={0.5} sx={{ minWidth: 0 }}>
          <Typography
            variant='overline'
            sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}
          >
            {eyebrow(question, index)}
          </Typography>
          <Typography
            component='b'
            sx={{ fontSize: 19, fontWeight: 700, lineHeight: 1.3 }}
          >
            {question.title}
          </Typography>
        </Stack>

        {tone === 'admin' && (
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ flex: 'none', textAlign: 'right', whiteSpace: 'pre-line' }}
          >
            {question.type === 'multi_choice'
              ? `${marks} ${declension(marks, [
                  __('označenie'),
                  __('označenia'),
                  __('označení'),
                ])}\n${votesCount} ${declension(votesCount, [
                  __('hlasujúci'),
                  __('hlasujúci'),
                  __('hlasujúcich'),
                ])}`
              : `${marks} ${declineAnswers(marks)}`}
          </Typography>
        )}
      </Stack>

      {question.description && (
        <Typography variant='body2' color='text.secondary'>
          {question.description}
        </Typography>
      )}

      {question.type === 'scale' ? (
        tone === 'admin' ? (
          <ScaleResultsTable question={question} />
        ) : (
          <ScaleResultsSummary question={question} />
        )
      ) : isTextOnly ? (
        textAnswers.length ? (
          <TextAnswers answers={textAnswers} compact={tone === 'citizen'} />
        ) : (
          <Typography variant='body2' color='text.secondary'>
            {__('Na túto otázku zatiaľ nikto neodpovedal.')}
          </Typography>
        )
      ) : (
        <>
          <ResultBars
            question={question}
            votesCount={votesCount}
            tone={tone}
            myOptionIds={myOptionIds}
            renderOptionFooter={
              tone === 'citizen'
                ? (option) =>
                    option.type === 'free' && textAnswers.length ? (
                      <Button
                        variant='text'
                        size='small'
                        sx={{ alignSelf: 'flex-start', px: 0 }}
                        onClick={() => setOpenAnswers((open) => !open)}
                      >
                        {openAnswers
                          ? __('Skryť otvorené odpovede')
                          : `${__('Zobraziť')} ${textAnswers.length} ${declension(
                              textAnswers.length,
                              [
                                __('otvorenú odpoveď'),
                                __('otvorené odpovede'),
                                __('otvorených odpovedí'),
                              ],
                            )}`}
                      </Button>
                    ) : null
                : undefined
            }
          />

          {/* Úradník ich má rozbalené vždy - je to jeho pracovný podklad;
              občanovi sa otvárajú odkazom pri možnosti „Iné". */}
          {textAnswers.length > 0 && (tone === 'admin' || openAnswers) && (
            <TextAnswers answers={textAnswers} compact={tone === 'citizen'} />
          )}
        </>
      )}
    </Card>
  );
};

/**
 * Výsledky hlasovania - tá istá zostava kariet pre občana aj pre úradníka,
 * líši sa len `tone`.
 *
 * Je to zámerne jeden komponent: kým to boli dve obrazovky, výsečový graf
 * v jednej a tabuľka v druhej, tie isté čísla vyzerali na každej inak a nedalo
 * sa povedať, ktorá z nich je tá pravá.
 */
const VotingResultsView: React.FC<Props> = ({
  questions,
  votesCount,
  tone,
  myVotes,
}) => {
  const myOptionIds = React.useMemo(() => {
    if (!myVotes?.length) return undefined;
    return new Set(
      myVotes.flatMap(({ options }) =>
        (options ?? []).map((option) => option.option_id),
      ),
    );
  }, [myVotes]);

  if (!votesCount) {
    return (
      <Card sx={{ p: 3 }}>
        <Typography variant='body1'>
          {__('V tomto hlasovaní zatiaľ nikto nehlasoval.')}
        </Typography>
      </Card>
    );
  }

  /**
   * **Hlasy sú, otázky nie.** Server vydá `questions: []` pri bežiacom
   * hlasovaní s vypnutými priebežnými výsledkami. Oba volajúci si to majú
   * odfiltrovať skôr (`detailedAvailable`), takže sem sa to dostane len keď
   * niekto tú podmienku obíde alebo dotaz spadne - a bez tohto návratu by
   * z toho bola prázdna biela plocha bez jediného slova.
   */
  if (!questions.length) {
    return (
      <Card sx={{ p: 3 }}>
        <Typography variant='body1'>
          {__(
            'Podrobné výsledky nie sú k dispozícii, zobrazuje sa len počet hlasov.',
          )}
        </Typography>
      </Card>
    );
  }

  return (
    <Stack gap={1.5}>
      {questions.map((question, index) => (
        <QuestionResultCard
          key={question.question_id}
          question={question}
          index={index}
          votesCount={votesCount}
          tone={tone}
          myOptionIds={myOptionIds}
        />
      ))}
    </Stack>
  );
};

export default VotingResultsView;
